import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyleKit } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { TableKit } from '@tiptap/extension-table';
import Figure, {
    findFigure,
    FIGURE_MEDIA_TYPES,
    FIGURE_WIDTHS,
    isSafeMediaSrc,
    Media,
    SafeImage,
} from './richTextFigure';
import { isRichTextEmpty, normalizeRichTextContent } from '../utils/richText';
import { relativizeRichTextMedia, resolveMediaUrl, resolveRichTextMedia } from '../utils/media';
import {
    HEIC_UNSUPPORTED_MESSAGE,
    isHeicFile,
    mediaKindForFile,
    stopsUploadBatch,
    summarizeUploadBatch,
    UPLOAD_NOT_TRIED_REASON,
} from '../utils/uploadLimits';
import { captureVideoPoster } from '../utils/videoPoster';
import {
    isAllowedEmbedSrc,
    isRumbleEmbedSrc,
    isSafeEmbedPoster,
    parseEmbedInput,
    resolveEmbedUrl,
} from '../utils/embeds';
import {
    AlignCenterIcon,
    AlignJustifyIcon,
    AlignLeftIcon,
    AlignRightIcon,
    AudioIcon,
    BulletListIcon,
    CaptionIcon,
    ClearFormatIcon,
    CodeBlockIcon,
    CodeIcon,
    EmbedIcon,
    FullWidthIcon,
    HighlightIcon,
    ImageIcon,
    LinkIcon,
    OrderedListIcon,
    QuoteIcon,
    RedoIcon,
    RuleIcon,
    TableIcon,
    TrashIcon,
    UndoIcon,
    VideoIcon,
    WrapCenterIcon,
    WrapLeftIcon,
    WrapRightIcon,
} from './richTextIcons';
import './RichTextEditor.css';

// Mid-tone palette picked to stay legible on both the dark and light themes,
// since text colors are stored as fixed inline styles.
const TEXT_COLORS = [
    { label: 'Signal red', value: '#ff6b6b' },
    { label: 'Brick', value: '#b83c3c' },
    { label: 'Amber', value: '#d4a017' },
    { label: 'Moss', value: '#4caf7d' },
    { label: 'Cold blue', value: '#4a9eda' },
    { label: 'Violet', value: '#b06fc9' },
    { label: 'Slate', value: '#8a8f98' },
];

// Translucent washes so highlights work over both theme backgrounds.
const HIGHLIGHT_COLORS = [
    { label: 'Red wash', value: 'rgba(255, 107, 107, 0.28)' },
    { label: 'Amber wash', value: 'rgba(212, 160, 23, 0.28)' },
    { label: 'Green wash', value: 'rgba(76, 175, 125, 0.28)' },
    { label: 'Blue wash', value: 'rgba(74, 158, 218, 0.28)' },
    { label: 'Violet wash', value: 'rgba(176, 111, 201, 0.28)' },
];

const FONT_FAMILIES = [
    { label: 'Default', value: '' },
    { label: 'Typewriter', value: "'Courier Prime', monospace" },
    { label: 'Terminal', value: "'JetBrains Mono', monospace" },
    { label: 'Serif', value: "Georgia, 'Times New Roman', serif" },
    { label: 'Sans', value: 'Helvetica, Arial, sans-serif' },
];

// Inline styles round-trip through the editor with quote styles flipped
// ('Courier Prime' → "Courier Prime"); compare families quote-insensitively.
const normalizeFontFamily = (value) => (value || '')
    .replace(/['"]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

// The body text renders at 0.95rem ≈ 15px on every surface; sizes are applied
// as explicit px so the number in the toolbar means what it says. Headings
// render larger (RichTextEditor.css), so "default" is per block type.
const DEFAULT_FONT_PX = 15;
const MIN_FONT_PX = 8;
const MAX_FONT_PX = 96;
const BLOCK_DEFAULT_PX = { p: 15, h1: 22, h2: 19, h3: 17, h4: 15 };

// em values are relative to the surrounding block and can't be resolved to px
// without layout context — return null so the stepper falls back to the block
// default instead of showing a misleading number.
const parseFontPx = (value) => {
    if (!value) {
        return null;
    }
    const match = /^([\d.]+)(px|rem)$/.exec(String(value).trim());
    if (!match) {
        return null;
    }
    const size = Number.parseFloat(match[1]);
    if (!Number.isFinite(size)) {
        return null;
    }
    return Math.round(match[2] === 'px' ? size : size * 16);
};

const BLOCK_OPTIONS = [
    { value: 'p', label: '¶', title: 'Paragraph' },
    { value: 'h1', label: 'H1', title: 'Heading 1' },
    { value: 'h2', label: 'H2', title: 'Heading 2' },
    { value: 'h3', label: 'H3', title: 'Heading 3' },
    { value: 'h4', label: 'H4', title: 'Heading 4' },
];

const TEXT_ALIGN_OPTIONS = [
    { value: 'left', title: 'Align left', Icon: AlignLeftIcon },
    { value: 'center', title: 'Align center', Icon: AlignCenterIcon },
    { value: 'right', title: 'Align right', Icon: AlignRightIcon },
    { value: 'justify', title: 'Justify', Icon: AlignJustifyIcon },
];

const TEXT_ALIGNMENTS = ['left', 'center', 'right', 'justify'];

const FIGURE_ALIGN_OPTIONS = [
    { value: 'left', title: 'Float left — text wraps on the right', Icon: WrapLeftIcon },
    { value: 'center', title: 'Centered block', Icon: WrapCenterIcon },
    { value: 'right', title: 'Float right — text wraps on the left', Icon: WrapRightIcon },
    { value: 'full', title: 'Full column width', Icon: FullWidthIcon },
];

const buildMediaAltText = (file) => {
    if (!file?.name) {
        return '';
    }
    return file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim();
};

// HEIC counts so a dropped/pasted iPhone photo gets its explanation instead
// of being ignored.
const containsMediaFile = (files) => Array.from(files || []).some(
    (file) => mediaKindForFile(file) !== null || isHeicFile(file)
);

// Upload handlers resolve with a URL string or an { url } object.
const uploadedUrl = (uploadResult) => (
    typeof uploadResult === 'string' ? uploadResult : uploadResult?.url
);

// One insert button per media kind; every kind lands as a figure with the
// same layout controls (alignment, width, caption, accessible label).
const MEDIA_BUTTONS = [
    { kind: 'image', title: 'Insert image', Icon: ImageIcon },
    { kind: 'video', title: 'Insert video', Icon: VideoIcon },
    { kind: 'audio', title: 'Insert audio track', Icon: AudioIcon },
];

const MEDIA_ACCEPT = {
    image: 'image/*',
    video: 'video/*',
    audio: 'audio/*',
};

const MEDIA_LABELS = {
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
    embed: 'Embed',
};

const stopFocusSteal = (event) => event.preventDefault();

// The editor reports colors as "rgb(r, g, b)" while swatches store hex/rgba
// strings; normalize both so active-swatch highlighting works.
const normalizeColor = (value) => {
    if (!value) {
        return '';
    }
    const hexMatch = /^#([0-9a-f]{6})$/i.exec(value.trim());
    if (hexMatch) {
        const int = parseInt(hexMatch[1], 16);
        return `rgb(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255})`;
    }
    return value.replace(/\s+/g, ' ').trim();
};

const ToolbarButton = ({ onClick, active, disabled, title, children, className }) => (
    <button
        type="button"
        onMouseDown={stopFocusSteal}
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-label={title}
        aria-pressed={active === undefined ? undefined : Boolean(active)}
        className={`${active ? 'active' : ''}${className ? ` ${className}` : ''}`}
    >
        {children}
    </button>
);

const RichTextEditor = ({
    value,
    onChange,
    placeholder = 'Begin writing your document...',
    className = '',
    onUploadImage,
}) => {
    const fileInputRef = useRef(null);
    const posterInputRef = useRef(null);
    const posterTargetSrcRef = useRef(null);
    const editorRef = useRef(null);
    const onUploadImageRef = useRef(onUploadImage);
    const pendingUploadCountRef = useRef(0);
    const pendingLookupCountRef = useRef(0);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [isFetchingEmbedDetails, setIsFetchingEmbedDetails] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [openPanel, setOpenPanel] = useState(null);
    const [sizeDraft, setSizeDraft] = useState(null);
    const [, forceUpdate] = useReducer((count) => count + 1, 0);

    useEffect(() => {
        onUploadImageRef.current = onUploadImage;
    }, [onUploadImage]);

    // Shared busy state for async media tasks the writer waits on (uploads,
    // posters, Rumble page-link lookups): the insert buttons stay disabled
    // until all have settled. Background embed-detail lookups don't count.
    const beginUploadTask = useCallback(() => {
        pendingUploadCountRef.current += 1;
        setIsUploadingMedia(true);
    }, []);

    const endUploadTask = useCallback(() => {
        pendingUploadCountRef.current -= 1;
        if (pendingUploadCountRef.current <= 0) {
            pendingUploadCountRef.current = 0;
            setIsUploadingMedia(false);
        }
    }, []);

    // Uploads a still captured from a just-inserted video and sets it as
    // that video's poster. Runs in the background so the batch moves on;
    // a failed capture is skipped silently (the player still works).
    const attachCapturedPoster = useCallback((posterPromise, videoSrc) => {
        beginUploadTask();
        (async () => {
            try {
                const posterFile = await posterPromise;
                const uploadMedia = onUploadImageRef.current;
                if (!posterFile || !uploadMedia) {
                    return;
                }
                const posterUrl = uploadedUrl(await uploadMedia(posterFile));
                if (posterUrl) {
                    // Not an undo step: the writer never asked for it.
                    editorRef.current?.commands.setVideoPoster({
                        src: videoSrc,
                        poster: resolveMediaUrl(posterUrl),
                        addToHistory: false,
                    });
                }
            } catch {
                // No poster; the player falls back to its first frame.
            } finally {
                endUploadTask();
            }
        })();
    }, [beginUploadTask, endUploadTask]);

    // Uploads media files (images, video, audio) and inserts them at the
    // selection (or at insertAt, e.g. a drop position). Each file is tried
    // on its own — one failure no longer drops the rest of the batch — and
    // a summary names every file that could not be added. Only a refusal
    // that would repeat for every file (no or wrong passphrase, rate
    // limited) stops the batch early. Snapshot files synchronously:
    // DataTransfer file lists are only readable while the originating event
    // is being handled.
    const insertUploadedFiles = useCallback(async (files, insertAt) => {
        const allFiles = Array.from(files || []);
        const editor = editorRef.current;
        const uploadMedia = onUploadImageRef.current;

        if (!editor || !uploadMedia || allFiles.length === 0) {
            return;
        }

        const failures = [];
        const mediaFiles = [];
        allFiles.forEach((file) => {
            if (isHeicFile(file)) {
                failures.push({ name: file.name, reason: HEIC_UNSUPPORTED_MESSAGE });
            } else if (mediaKindForFile(file) === null) {
                failures.push({ name: file.name, reason: 'not a photo, video or audio file' });
            } else {
                mediaFiles.push(file);
            }
        });

        setUploadError(null);
        beginUploadTask();

        let added = 0;
        try {
            if (typeof insertAt === 'number' && mediaFiles.length > 0) {
                editor.chain().focus(insertAt).run();
            }

            for (const [index, file] of mediaFiles.entries()) {
                if (mediaFiles.length > 1) {
                    setUploadProgress(`Uploading ${index + 1} of ${mediaFiles.length}…`);
                }
                const kind = mediaKindForFile(file);
                // Read the poster frame from the local file while it uploads.
                const posterPromise = kind === 'video' ? captureVideoPoster(file) : null;
                try {
                    const mediaUrl = uploadedUrl(await uploadMedia(file));
                    if (!mediaUrl) {
                        throw new Error('no media URL returned');
                    }

                    // Insert the resolved URL so the media displays inside
                    // the editor; onUpdate relativizes it again before
                    // persisting. New media arrives as a figure so a caption
                    // can be written right away.
                    const src = resolveMediaUrl(mediaUrl);
                    const altText = buildMediaAltText(file);
                    editor.chain().focus().setFigure({
                        mediaType: kind,
                        src,
                        ...(altText ? { alt: altText } : {}),
                    }).run();
                    added += 1;

                    if (posterPromise) {
                        attachCapturedPoster(posterPromise, src);
                    }
                } catch (error) {
                    failures.push({ name: file.name, reason: error?.message || 'upload failed' });
                    // The rest would be refused the same way: send no more
                    // requests bound to fail and list them as not tried.
                    if (stopsUploadBatch(error)) {
                        mediaFiles.slice(index + 1).forEach((rest) => {
                            failures.push({ name: rest.name, reason: UPLOAD_NOT_TRIED_REASON });
                        });
                        break;
                    }
                }
            }
        } finally {
            setUploadProgress(null);
            setUploadError(summarizeUploadBatch({ total: allFiles.length, added, failures }));
            endUploadTask();
        }
    }, [attachCapturedPoster, beginUploadTask, endUploadTask]);

    // Looks up a just-inserted Rumble embed link's thumbnail and title and
    // fills them in as the figure's poster and accessible title (the site
    // shows that poster in a click-to-play facade; Rumble embed ids can't
    // be turned into a still at render time). Background work, like the
    // captured video poster: not an undo step, and a failed lookup is
    // skipped silently — the embed plays without a poster. The embed is
    // already in place, so the toolbar stays usable meanwhile; only a quiet
    // status shows while lookups are pending.
    const attachEmbedDetails = useCallback((src) => {
        pendingLookupCountRef.current += 1;
        setIsFetchingEmbedDetails(true);
        (async () => {
            try {
                const { title, thumbnailUrl } = await resolveEmbedUrl(src);
                editorRef.current?.commands.fillEmbedDetails({
                    src,
                    poster: thumbnailUrl,
                    title,
                    addToHistory: false,
                });
            } catch {
                // No poster; the reader gets Rumble's own player.
            } finally {
                pendingLookupCountRef.current = Math.max(0, pendingLookupCountRef.current - 1);
                if (pendingLookupCountRef.current === 0) {
                    setIsFetchingEmbedDetails(false);
                }
            }
        })();
    }, []);

    // Inserts a parsed embed link (from the toolbar prompt or a paste).
    // Ready embed URLs insert synchronously (a Rumble one then has its
    // poster and title filled in the background); Rumble page links
    // resolve through the backend first — embed URL, thumbnail and title in
    // one lookup — sharing the upload busy state.
    const insertEmbed = useCallback(async (parsed) => {
        const editor = editorRef.current;
        if (!editor || !parsed) {
            return;
        }

        setUploadError(null);
        if (parsed.type === 'embed') {
            editor.chain().focus().setFigure({ mediaType: 'embed', src: parsed.src }).run();
            if (isRumbleEmbedSrc(parsed.src)) {
                attachEmbedDetails(parsed.src);
            }
            return;
        }

        beginUploadTask();
        try {
            const { embedUrl, title, thumbnailUrl } = await resolveEmbedUrl(parsed.url);
            editor.chain().focus().setFigure({
                mediaType: 'embed',
                src: embedUrl,
                ...(thumbnailUrl ? { poster: thumbnailUrl } : {}),
                ...(title ? { alt: title } : {}),
            }).run();
        } catch (error) {
            setUploadError(error?.message || 'Could not embed that video');
        } finally {
            endUploadTask();
        }
    }, [attachEmbedDetails, beginUploadTask, endUploadTask]);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4],
                },
                link: {
                    openOnClick: false,
                    autolink: true,
                    defaultProtocol: 'https',
                },
            }),
            Placeholder.configure({
                placeholder: ({ node }) => (
                    node.type.name === 'figure' ? 'Write a caption…' : placeholder
                ),
                includeChildren: true,
            }),
            SafeImage.configure({
                HTMLAttributes: {
                    class: 'rich-text-editor__image',
                },
            }),
            TextStyleKit,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Subscript,
            Superscript,
            // renderWrapper keeps a div.tableWrapper around tables in both
            // the editor DOM and the saved HTML — the wrapper is the BFC that
            // lets tables sit beside floated figures instead of dropping
            // below them.
            TableKit.configure({
                table: {
                    renderWrapper: true,
                },
            }),
            Figure,
            Media,
        ],
        content: resolveRichTextMedia(normalizeRichTextContent(value)),
        onUpdate: ({ editor: instance }) => {
            if (typeof onChange !== 'function') {
                return;
            }

            // Store portable server-relative image paths (same policy as
            // thumbnails) so content survives host/protocol changes.
            // Emptiness is judged on the serialized HTML via the app's own
            // media-aware check: editor.isEmpty wrongly reports true for a
            // document whose only content is a caption-less figure, which
            // would emit '' and wipe the image. A table still being filled in
            // is likewise kept alive here — but only here, so the publish
            // gate and description fallback still treat an empty grid as
            // empty content.
            const html = relativizeRichTextMedia(instance.getHTML());
            const keepStructure = /<table\b/i.test(html);
            onChange(!keepStructure && isRichTextEmpty(html) ? '' : html);
        },
        editorProps: {
            attributes: {
                class: 'rich-text-editor__input',
                spellCheck: 'true',
            },
            handleDrop: (view, event, _slice, moved) => {
                if (moved || !onUploadImageRef.current) {
                    return false;
                }

                const files = event.dataTransfer?.files;
                if (!containsMediaFile(files)) {
                    return false;
                }

                event.preventDefault();
                const coords = view.posAtCoords({ left: event.clientX, top: event.clientY });
                insertUploadedFiles(files, coords?.pos);
                return true;
            },
            handlePaste: (view, event) => {
                // A lone video link pasted into an empty paragraph becomes an
                // embed. Pasted into text (e.g. a bibliography entry) it stays
                // a link, so literal URLs remain possible; the toolbar button
                // covers those spots.
                const files = event.clipboardData?.files;
                if (!files?.length) {
                    const text = event.clipboardData?.getData('text/plain')?.trim();
                    const { selection } = view.state;
                    const inEmptyParagraph = selection.empty
                        && selection.$from.parent.type.name === 'paragraph'
                        && selection.$from.parent.content.size === 0;
                    if (text && inEmptyParagraph) {
                        const parsed = parseEmbedInput(text);
                        if (parsed) {
                            event.preventDefault();
                            insertEmbed(parsed);
                            return true;
                        }
                    }
                    return false;
                }

                if (!onUploadImageRef.current || !containsMediaFile(files)) {
                    return false;
                }

                event.preventDefault();
                insertUploadedFiles(files);
                return true;
            },
        },
    });

    useEffect(() => {
        editorRef.current = editor;
    }, [editor]);

    const handleMediaButtonClick = (kind) => {
        setUploadError(null);

        if (!editor) {
            return;
        }

        if (!onUploadImage) {
            const url = window.prompt(`Enter ${kind} URL`);
            if (!url) {
                return;
            }

            editor.chain().focus().setFigure({
                mediaType: kind,
                src: resolveMediaUrl(url.trim()),
            }).run();
            return;
        }

        const input = fileInputRef.current;
        if (input) {
            // The accept filter follows the button that was clicked;
            // handleFileSelection keys off each file's real MIME type, so a
            // broader pick still inserts correctly.
            input.setAttribute('accept', MEDIA_ACCEPT[kind]);
            input.click();
        }
    };

    const handleEmbedButtonClick = () => {
        setUploadError(null);

        if (!editor) {
            return;
        }

        const input = window.prompt('Paste a YouTube or Rumble video link');
        if (!input || !input.trim()) {
            return;
        }

        const parsed = parseEmbedInput(input.trim());
        if (!parsed) {
            setUploadError('Only YouTube and Rumble video links can be embedded');
            return;
        }

        insertEmbed(parsed);
    };

    const handleFileSelection = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';

        if (files.length === 0) {
            return;
        }

        await insertUploadedFiles(files);
    };

    // Poster image chosen for the selected video (media bar "Poster…").
    // Applied by src once the upload finishes, wherever the selection is.
    const handlePosterFileSelection = async (event) => {
        const [file] = Array.from(event.target.files || []);
        event.target.value = '';
        const videoSrc = posterTargetSrcRef.current;
        const uploadMedia = onUploadImageRef.current;

        if (!file || !videoSrc || !uploadMedia) {
            return;
        }
        if (isHeicFile(file)) {
            setUploadError(HEIC_UNSUPPORTED_MESSAGE);
            return;
        }
        if (mediaKindForFile(file) !== 'image') {
            setUploadError('Choose an image file for the poster');
            return;
        }

        setUploadError(null);
        beginUploadTask();
        try {
            const posterUrl = uploadedUrl(await uploadMedia(file));
            if (!posterUrl) {
                throw new Error('Upload failed: no media URL returned');
            }
            const poster = resolveMediaUrl(posterUrl);
            // Embed posters show on the public page in place of the player,
            // so they must be https (setVideoPoster would skip it anyway).
            if (isAllowedEmbedSrc(videoSrc) && !isSafeEmbedPoster(poster)) {
                throw new Error('the uploaded image is not served over https');
            }
            // No focus(): the writer may have moved to another field.
            editorRef.current?.commands.setVideoPoster({
                src: videoSrc,
                poster,
            });
        } catch (error) {
            setUploadError(`Couldn't set the poster: ${error?.message || 'upload failed'}`);
        } finally {
            endUploadTask();
        }
    };

    useEffect(() => {
        if (!editor) {
            return undefined;
        }

        const handleEditorChange = () => {
            forceUpdate();
        };

        editor.on('transaction', handleEditorChange);
        editor.on('selectionUpdate', handleEditorChange);
        editor.on('focus', handleEditorChange);
        editor.on('blur', handleEditorChange);

        return () => {
            editor.off('transaction', handleEditorChange);
            editor.off('selectionUpdate', handleEditorChange);
            editor.off('focus', handleEditorChange);
            editor.off('blur', handleEditorChange);
        };
    }, [editor, forceUpdate]);

    useEffect(() => {
        if (!editor) {
            return;
        }

        // Compare in resolved form: the editor holds absolute media URLs while
        // the parent value stores relative paths.
        const normalized = resolveRichTextMedia(normalizeRichTextContent(value));
        const current = editor.getHTML();

        if (normalized !== current) {
            editor.commands.setContent(normalized, { emitUpdate: false });
        }
    }, [editor, value]);

    if (!editor) {
        return (
            <div className={`rich-text-editor${className ? ` ${className}` : ''}`}>
                <div className="rich-text-editor__loading">Loading editor…</div>
            </div>
        );
    }

    const currentBlock = (() => {
        for (let level = 1; level <= 4; level += 1) {
            if (editor.isActive('heading', { level })) {
                return `h${level}`;
            }
        }
        return 'p';
    })();

    const isFigureActive = editor.isActive('figure');
    const isImageActive = editor.isActive('image');
    const isMediaNodeActive = editor.isActive('media');
    const isTableActive = editor.isActive('table');
    const figureAttrs = isFigureActive ? editor.getAttributes('figure') : {};

    // Which media kind the selection sits on (null when none) — drives the
    // shared layout bar and the insert buttons' active state.
    const activeMediaType = (() => {
        if (isFigureActive) {
            return FIGURE_MEDIA_TYPES.includes(figureAttrs.mediaType)
                ? figureAttrs.mediaType
                : 'image';
        }
        if (isImageActive) {
            return 'image';
        }
        if (isMediaNodeActive) {
            const nodeType = editor.getAttributes('media').mediaType;
            return FIGURE_MEDIA_TYPES.includes(nodeType) ? nodeType : 'video';
        }
        return null;
    })();

    const handleBlockClick = (next) => {
        // Applied inside a caption these would convert the figure textblock
        // itself and destroy the image.
        if (editor.isActive('figure')) {
            return;
        }

        if (next === 'p') {
            editor.chain().focus().setParagraph().run();
        } else {
            editor.chain().focus().setHeading({ level: Number(next.slice(1)) }).run();
        }
    };

    const textStyleAttrs = editor.getAttributes('textStyle');
    const currentFontFamily = textStyleAttrs.fontFamily || '';
    const currentFontSize = textStyleAttrs.fontSize || '';
    const currentFontPx = parseFontPx(currentFontSize);
    const currentColor = textStyleAttrs.color || '';
    const currentHighlight = textStyleAttrs.backgroundColor || '';
    const currentAlign = TEXT_ALIGNMENTS.find(
        (align) => editor.isActive({ textAlign: align })
    ) || 'left';

    const applyFontFamily = (next) => {
        if (next) {
            editor.chain().focus().setFontFamily(next).run();
        } else {
            editor.chain().focus().unsetFontFamily().run();
        }
    };

    const matchedFont = FONT_FAMILIES.find(
        (font) => normalizeFontFamily(font.value) === normalizeFontFamily(currentFontFamily)
    );
    const isCustomFont = Boolean(currentFontFamily) && !matchedFont;
    const fontButtonLabel = isCustomFont
        ? 'Custom'
        : (matchedFont && matchedFont.value ? matchedFont.label : 'Font');

    // Default size for the block the caret is in — "15" only means "unset"
    // inside paragraphs; headings have larger defaults.
    const blockDefaultPx = BLOCK_DEFAULT_PX[currentBlock] ?? DEFAULT_FONT_PX;

    // refocus: false lets a blur-triggered commit apply without yanking focus
    // back from whatever field the user just clicked (TipTap defers focus to
    // the next frame, which would otherwise win over the new field).
    const commitFontSize = (raw, { refocus = true } = {}) => {
        const chain = () => (refocus ? editor.chain().focus() : editor.chain());
        const trimmed = String(raw ?? '').trim();
        if (!trimmed) {
            chain().unsetFontSize().run();
            return;
        }

        const parsed = Math.round(Number(trimmed));
        if (!Number.isFinite(parsed)) {
            return;
        }

        const clamped = Math.min(Math.max(parsed, MIN_FONT_PX), MAX_FONT_PX);
        if (clamped === blockDefaultPx) {
            chain().unsetFontSize().run();
        } else {
            chain().setFontSize(`${clamped}px`).run();
        }
    };

    const stepFontSize = (delta) => {
        // A typed-but-uncommitted value is the user's intent — step from it
        // rather than silently discarding it.
        const draftPx = sizeDraft !== null ? Math.round(Number(String(sizeDraft).trim())) : null;
        setSizeDraft(null);
        if (Number.isFinite(draftPx) && draftPx > 0) {
            commitFontSize(draftPx + delta);
            return;
        }

        const { state } = editor;
        const { from, to, empty } = state.selection;
        if (empty) {
            commitFontSize((currentFontPx ?? blockDefaultPx) + delta);
            return;
        }

        // Mixed-size selections: step each text run relative to its own size
        // instead of flattening the whole range to firstSize ± delta.
        const runs = [];
        state.doc.nodesBetween(from, to, (node, pos) => {
            if (!node.isText) {
                return;
            }
            const mark = node.marks.find((m) => m.type.name === 'textStyle');
            const $pos = state.doc.resolve(pos);
            const parent = $pos.parent;
            const blockKey = parent.type.name === 'heading' ? `h${parent.attrs.level}` : 'p';
            const runDefault = BLOCK_DEFAULT_PX[blockKey] ?? DEFAULT_FONT_PX;
            runs.push({
                from: Math.max(pos, from),
                to: Math.min(pos + node.nodeSize, to),
                px: parseFontPx(mark?.attrs?.fontSize) ?? runDefault,
                defaultPx: runDefault,
            });
        });

        if (runs.length === 0) {
            commitFontSize((currentFontPx ?? blockDefaultPx) + delta);
            return;
        }

        let chain = editor.chain().focus();
        runs.forEach((run) => {
            const next = Math.min(Math.max(run.px + delta, MIN_FONT_PX), MAX_FONT_PX);
            chain = chain.setTextSelection({ from: run.from, to: run.to });
            chain = next === run.defaultPx
                ? chain.unsetFontSize()
                : chain.setFontSize(`${next}px`);
        });
        chain.setTextSelection({ from, to }).run();
    };

    const handleAlignClick = (align) => {
        editor.chain().focus().setTextAlign(align).run();
    };

    const handleLinkButtonClick = () => {
        if (editor.isActive('link')) {
            editor.chain().focus().unsetLink().run();
            return;
        }

        const url = window.prompt('Link URL', 'https://');
        if (url === null) {
            return;
        }

        const trimmed = url.trim();
        if (!trimmed || trimmed === 'https://') {
            return;
        }

        editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
    };

    const applyColor = (colorValue) => {
        if (colorValue) {
            editor.chain().focus().setColor(colorValue).run();
        } else {
            editor.chain().focus().unsetColor().run();
        }
    };

    const applyHighlight = (colorValue) => {
        if (colorValue) {
            editor.chain().focus().setBackgroundColor(colorValue).run();
        } else {
            editor.chain().focus().unsetBackgroundColor().run();
        }
    };

    const handleCaptionToggle = () => {
        if (isFigureActive) {
            editor.chain().focus().figureToMedia().run();
        } else {
            editor.chain().focus().mediaToFigure().run();
        }
    };

    const handleAltEdit = () => {
        const nodeName = isImageActive ? 'image' : 'media';
        const currentAlt = isFigureActive
            ? (figureAttrs.alt || '')
            : (editor.getAttributes(nodeName).alt || '');
        const promptLabel = activeMediaType === 'image'
            ? 'Image alt text (for screen readers)'
            : (activeMediaType === 'embed'
                ? 'Accessible title (for screen readers)'
                : 'Accessible description (for screen readers)');
        const nextAlt = window.prompt(promptLabel, currentAlt);
        if (nextAlt === null) {
            return;
        }

        if (isFigureActive) {
            editor.chain().focus().applyFigure({ alt: nextAlt.trim() || null }).run();
        } else {
            editor.chain().focus().updateAttributes(nodeName, { alt: nextAlt.trim() || null }).run();
        }
    };

    // Videos and embeds both take a poster (an embed's is its data-poster:
    // the still the site's click-to-play facade shows instead of a
    // YouTube-derived or Rumble-captured one).
    const isEmbedSelected = activeMediaType === 'embed';
    const selectedVideo = (activeMediaType === 'video' || isEmbedSelected)
        ? (isFigureActive ? figureAttrs : editor.getAttributes('media'))
        : null;
    const currentPoster = selectedVideo?.poster || '';

    const handlePosterPick = () => {
        setUploadError(null);
        const videoSrc = selectedVideo?.src;
        if (!videoSrc) {
            return;
        }

        if (!onUploadImage) {
            const url = window.prompt('Poster image URL (shown before the video plays)', currentPoster);
            if (url === null) {
                return;
            }
            const trimmed = url.trim();
            if (trimmed && !isSafeMediaSrc(trimmed)) {
                setUploadError('Use an http(s) image URL for the poster');
                return;
            }
            if (trimmed && isEmbedSelected && !isSafeEmbedPoster(resolveMediaUrl(trimmed))) {
                setUploadError('Use an https image URL for the poster');
                return;
            }
            editor.chain().focus().setVideoPoster({
                src: videoSrc,
                poster: trimmed ? resolveMediaUrl(trimmed) : null,
            }).run();
            return;
        }

        posterTargetSrcRef.current = videoSrc;
        posterInputRef.current?.click();
    };

    const handlePosterRemove = () => {
        if (selectedVideo?.src) {
            editor.chain().focus().setVideoPoster({ src: selectedVideo.src, poster: null }).run();
        }
    };

    const handleMediaRemove = () => {
        if (isFigureActive) {
            editor.chain().focus().deleteFigure().run();
        } else {
            editor.chain().focus().deleteSelection().run();
        }
    };

    const handleRuleClick = () => {
        // A rule dropped mid-caption would split the figure into two copies
        // of the image; hop out and insert it after the figure instead.
        const figure = findFigure(editor.state);
        if (figure) {
            editor.chain().focus().insertContentAt(
                figure.pos + figure.node.nodeSize,
                { type: 'horizontalRule' }
            ).run();
        } else {
            editor.chain().focus().setHorizontalRule().run();
        }
    };

    const colorPanel = openPanel === 'color';
    const panelColors = colorPanel ? TEXT_COLORS : HIGHLIGHT_COLORS;
    const panelCurrent = colorPanel ? currentColor : currentHighlight;
    const applyPanelColor = colorPanel ? applyColor : applyHighlight;

    return (
        <div className={`rich-text-editor${className ? ` ${className}` : ''}`}>
            <div className="rich-text-editor__toolbar" role="toolbar" aria-label="Formatting options">
                <div className="rich-text-editor__group">
                    <div className="rich-text-editor__segmented" role="group" aria-label="Block style">
                        {BLOCK_OPTIONS.map((option) => (
                            <ToolbarButton
                                key={option.value}
                                onClick={() => handleBlockClick(option.value)}
                                active={currentBlock === option.value && !isFigureActive}
                                disabled={isFigureActive}
                                title={isFigureActive ? 'Not available inside a media caption' : option.title}
                            >
                                {option.label}
                            </ToolbarButton>
                        ))}
                    </div>
                </div>
                <div className="rich-text-editor__divider" aria-hidden="true" />
                <div className="rich-text-editor__group">
                    <ToolbarButton
                        onClick={() => setOpenPanel(openPanel === 'font' ? null : 'font')}
                        active={openPanel === 'font' || Boolean(currentFontFamily)}
                        title="Font family"
                        className="rich-text-editor__font-btn"
                    >
                        <span style={currentFontFamily ? { fontFamily: currentFontFamily } : undefined}>
                            {fontButtonLabel}
                        </span>
                        <span className="rich-text-editor__font-caret" aria-hidden="true">▾</span>
                    </ToolbarButton>
                    <div className="rich-text-editor__size" role="group" aria-label="Font size">
                        <button
                            type="button"
                            onMouseDown={stopFocusSteal}
                            onClick={() => stepFontSize(-1)}
                            title="Decrease font size"
                            aria-label="Decrease font size"
                        >
                            −
                        </button>
                        <input
                            type="number"
                            className="rich-text-editor__size-input"
                            min={MIN_FONT_PX}
                            max={MAX_FONT_PX}
                            value={sizeDraft ?? (currentFontPx ?? '')}
                            placeholder={String(blockDefaultPx)}
                            onChange={(event) => setSizeDraft(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    // badInput: the field holds text a number
                                    // input can't represent — revert rather
                                    // than treating '' as "clear the size".
                                    if (!event.target.validity?.badInput) {
                                        commitFontSize(sizeDraft ?? currentFontPx);
                                    }
                                    setSizeDraft(null);
                                }
                            }}
                            onBlur={(event) => {
                                if (sizeDraft !== null) {
                                    if (!event.target.validity?.badInput) {
                                        commitFontSize(sizeDraft, { refocus: false });
                                    }
                                    setSizeDraft(null);
                                }
                            }}
                            title="Font size in pixels"
                            aria-label="Font size in pixels"
                        />
                        <button
                            type="button"
                            onMouseDown={stopFocusSteal}
                            onClick={() => stepFontSize(1)}
                            title="Increase font size"
                            aria-label="Increase font size"
                        >
                            +
                        </button>
                    </div>
                </div>
                <div className="rich-text-editor__divider" aria-hidden="true" />
                <div className="rich-text-editor__group">
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        disabled={!editor.isEditable || !editor.can().chain().focus().toggleBold().run()}
                        active={editor.isActive('bold')}
                        title="Bold (Ctrl+B)"
                        className="rich-text-editor__btn-bold"
                    >
                        B
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        disabled={!editor.isEditable || !editor.can().chain().focus().toggleItalic().run()}
                        active={editor.isActive('italic')}
                        title="Italic (Ctrl+I)"
                        className="rich-text-editor__btn-italic"
                    >
                        I
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        disabled={!editor.isEditable || !editor.can().chain().focus().toggleUnderline().run()}
                        active={editor.isActive('underline')}
                        title="Underline (Ctrl+U)"
                        className="rich-text-editor__btn-underline"
                    >
                        U
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        disabled={!editor.isEditable || !editor.can().chain().focus().toggleStrike().run()}
                        active={editor.isActive('strike')}
                        title="Strikethrough"
                        className="rich-text-editor__btn-strike"
                    >
                        S
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleSubscript().run()}
                        active={editor.isActive('subscript')}
                        title="Subscript"
                    >
                        X₂
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleSuperscript().run()}
                        active={editor.isActive('superscript')}
                        title="Superscript"
                    >
                        X²
                    </ToolbarButton>
                </div>
                <div className="rich-text-editor__divider" aria-hidden="true" />
                <div className="rich-text-editor__group">
                    <ToolbarButton
                        onClick={() => setOpenPanel(openPanel === 'color' ? null : 'color')}
                        active={openPanel === 'color' || Boolean(currentColor)}
                        title="Text color"
                    >
                        <span className="rich-text-editor__color-letter">
                            A
                            <span
                                className="rich-text-editor__color-bar"
                                style={{ background: currentColor || 'currentColor' }}
                            />
                        </span>
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => setOpenPanel(openPanel === 'highlight' ? null : 'highlight')}
                        active={openPanel === 'highlight' || Boolean(currentHighlight)}
                        title="Highlight color"
                    >
                        <span className="rich-text-editor__color-letter">
                            <HighlightIcon />
                            <span
                                className="rich-text-editor__color-bar"
                                style={{ background: currentHighlight || 'currentColor' }}
                            />
                        </span>
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={handleLinkButtonClick}
                        active={editor.isActive('link')}
                        title={editor.isActive('link') ? 'Remove link' : 'Add link'}
                    >
                        <LinkIcon />
                    </ToolbarButton>
                </div>
                <div className="rich-text-editor__divider" aria-hidden="true" />
                <div className="rich-text-editor__group">
                    {TEXT_ALIGN_OPTIONS.map(({ value: align, title, Icon: AlignIcon }) => (
                        <ToolbarButton
                            key={align}
                            onClick={() => handleAlignClick(align)}
                            active={currentAlign === align}
                            title={title}
                        >
                            <AlignIcon />
                        </ToolbarButton>
                    ))}
                </div>
                <div className="rich-text-editor__divider" aria-hidden="true" />
                <div className="rich-text-editor__group">
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        disabled={isFigureActive}
                        active={editor.isActive('bulletList')}
                        title="Bullet list"
                    >
                        <BulletListIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        disabled={isFigureActive}
                        active={editor.isActive('orderedList')}
                        title="Numbered list"
                    >
                        <OrderedListIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        disabled={isFigureActive}
                        active={editor.isActive('blockquote')}
                        title="Block quote"
                    >
                        <QuoteIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        active={editor.isActive('code')}
                        title="Inline code"
                    >
                        <CodeIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        disabled={isFigureActive}
                        active={editor.isActive('codeBlock')}
                        title="Code block"
                    >
                        <CodeBlockIcon />
                    </ToolbarButton>
                    <ToolbarButton onClick={handleRuleClick} title="Horizontal rule">
                        <RuleIcon />
                    </ToolbarButton>
                </div>
                <div className="rich-text-editor__divider" aria-hidden="true" />
                <div className="rich-text-editor__group">
                    {MEDIA_BUTTONS.map(({ kind, title, Icon: MediaIcon }) => (
                        <button
                            key={kind}
                            type="button"
                            onMouseDown={stopFocusSteal}
                            onClick={() => handleMediaButtonClick(kind)}
                            disabled={!editor.isEditable || (Boolean(onUploadImage) && isUploadingMedia)}
                            className={`${activeMediaType === kind ? 'active' : ''}${isUploadingMedia ? ' uploading' : ''}`}
                            title={isUploadingMedia ? 'Uploading…' : title}
                            aria-label={isUploadingMedia ? 'Uploading media' : title}
                            aria-pressed={activeMediaType === kind}
                        >
                            <MediaIcon />
                        </button>
                    ))}
                    <ToolbarButton
                        onClick={handleEmbedButtonClick}
                        disabled={!editor.isEditable || isUploadingMedia}
                        active={activeMediaType === 'embed'}
                        title="Embed a YouTube or Rumble video"
                    >
                        <EmbedIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                        disabled={isFigureActive || isTableActive}
                        active={isTableActive}
                        title={isTableActive
                            ? 'Already in a table — use the table bar below'
                            : (isFigureActive ? 'Not available inside a media caption' : 'Insert table (3×3)')}
                    >
                        <TableIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().unsetAllMarks().run()}
                        title="Clear formatting"
                    >
                        <ClearFormatIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().chain().focus().undo().run()}
                        title="Undo (Ctrl+Z)"
                    >
                        <UndoIcon />
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().chain().focus().redo().run()}
                        title="Redo (Ctrl+Shift+Z)"
                    >
                        <RedoIcon />
                    </ToolbarButton>
                </div>
            </div>
            {openPanel === 'font' && (
                <div className="rich-text-editor__panel" role="group" aria-label="Font family">
                    <span className="rich-text-editor__panel-label">Font</span>
                    {FONT_FAMILIES.map((font) => (
                        <ToolbarButton
                            key={font.label}
                            onClick={() => applyFontFamily(font.value)}
                            active={font === matchedFont || (!font.value && !currentFontFamily)}
                            title={font.value ? font.label : 'Default body font'}
                            className="rich-text-editor__font-option"
                        >
                            <span style={font.value ? { fontFamily: font.value } : undefined}>
                                {font.label}
                            </span>
                        </ToolbarButton>
                    ))}
                    {isCustomFont && (
                        <ToolbarButton
                            active
                            onClick={() => {}}
                            title="Font carried over from pasted content"
                            className="rich-text-editor__font-option"
                        >
                            <span style={{ fontFamily: currentFontFamily }}>Custom</span>
                        </ToolbarButton>
                    )}
                    <button
                        type="button"
                        onMouseDown={stopFocusSteal}
                        onClick={() => setOpenPanel(null)}
                        className="rich-text-editor__panel-close"
                        aria-label="Close font panel"
                    >
                        ✕
                    </button>
                </div>
            )}
            {(openPanel === 'color' || openPanel === 'highlight') && (
                <div className="rich-text-editor__panel" role="group" aria-label={colorPanel ? 'Text color' : 'Highlight color'}>
                    <span className="rich-text-editor__panel-label">
                        {colorPanel ? 'Text color' : 'Highlight'}
                    </span>
                    {panelColors.map((swatch) => (
                        <button
                            key={swatch.value}
                            type="button"
                            onMouseDown={stopFocusSteal}
                            onClick={() => applyPanelColor(swatch.value)}
                            className={`rich-text-editor__swatch${normalizeColor(panelCurrent) === normalizeColor(swatch.value) ? ' active' : ''}`}
                            style={{ background: swatch.value }}
                            title={swatch.label}
                            aria-label={swatch.label}
                        />
                    ))}
                    <label className="rich-text-editor__swatch-custom" title="Custom color">
                        <input
                            type="color"
                            onChange={(event) => applyPanelColor(event.target.value)}
                            aria-label="Custom color"
                        />
                        +
                    </label>
                    <button
                        type="button"
                        onMouseDown={stopFocusSteal}
                        onClick={() => applyPanelColor('')}
                        className="rich-text-editor__panel-clear"
                    >
                        None
                    </button>
                    <button
                        type="button"
                        onMouseDown={stopFocusSteal}
                        onClick={() => setOpenPanel(null)}
                        className="rich-text-editor__panel-close"
                        aria-label="Close color panel"
                    >
                        ✕
                    </button>
                </div>
            )}
            {activeMediaType && (
                <div className="rich-text-editor__imagebar" role="group" aria-label="Media layout">
                    <span className="rich-text-editor__panel-label">{MEDIA_LABELS[activeMediaType]}</span>
                    {FIGURE_ALIGN_OPTIONS.map(({ value: align, title, Icon: FigureIcon }) => (
                        <ToolbarButton
                            key={align}
                            onClick={() => editor.chain().focus().applyFigure({ align }).run()}
                            active={isFigureActive && figureAttrs.align === align}
                            title={title}
                        >
                            <FigureIcon />
                        </ToolbarButton>
                    ))}
                    <span className="rich-text-editor__imagebar-sep" aria-hidden="true" />
                    {FIGURE_WIDTHS.map((width) => (
                        <ToolbarButton
                            key={width}
                            onClick={() => editor.chain().focus().applyFigure(
                                figureAttrs.align === 'full' ? { width, align: 'center' } : { width }
                            ).run()}
                            active={isFigureActive && figureAttrs.width === width}
                            title={`Width ${width}% of the column`}
                        >
                            {width}%
                        </ToolbarButton>
                    ))}
                    <span className="rich-text-editor__imagebar-sep" aria-hidden="true" />
                    <ToolbarButton
                        onClick={handleCaptionToggle}
                        active={isFigureActive}
                        title={isFigureActive
                            ? `Remove caption (plain ${activeMediaType})`
                            : `Add a caption below the ${activeMediaType}`}
                    >
                        <CaptionIcon />
                    </ToolbarButton>
                    <ToolbarButton onClick={handleAltEdit} title="Edit alt text">
                        ALT
                    </ToolbarButton>
                    {selectedVideo && (
                        <>
                            <span className="rich-text-editor__imagebar-sep" aria-hidden="true" />
                            <ToolbarButton
                                onClick={handlePosterPick}
                                active={Boolean(currentPoster)}
                                disabled={Boolean(onUploadImage) && isUploadingMedia}
                                title={currentPoster
                                    ? 'Replace the poster image shown before the video plays'
                                    : 'Choose a poster image to show before the video plays'}
                            >
                                <ImageIcon />
                                Poster…
                            </ToolbarButton>
                            {currentPoster && (
                                <ToolbarButton
                                    onClick={handlePosterRemove}
                                    title={isEmbedSelected
                                        ? 'Remove the poster (YouTube shows its own thumbnail; Rumble loads its player)'
                                        : 'Remove the poster (the player shows the video\'s first frame)'}
                                >
                                    Remove poster
                                </ToolbarButton>
                            )}
                        </>
                    )}
                    <ToolbarButton
                        onClick={handleMediaRemove}
                        title={`Remove ${activeMediaType}`}
                        className="rich-text-editor__btn-danger"
                    >
                        <TrashIcon />
                    </ToolbarButton>
                </div>
            )}
            {isTableActive && (
                <div className="rich-text-editor__imagebar rich-text-editor__tablebar" role="group" aria-label="Table editing">
                    <span className="rich-text-editor__panel-label">Table</span>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().addRowAfter().run()}
                        title="Add row below"
                        className="rich-text-editor__table-op"
                    >
                        +Row
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => {
                            // Deleting the last remaining row fails inside
                            // prosemirror-tables; treat it as deleting the
                            // whole table.
                            if (!editor.chain().focus().deleteRow().run()) {
                                editor.chain().focus().deleteTable().run();
                            }
                        }}
                        title="Delete current row"
                        className="rich-text-editor__table-op"
                    >
                        −Row
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().addColumnAfter().run()}
                        title="Add column to the right"
                        className="rich-text-editor__table-op"
                    >
                        +Col
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => {
                            if (!editor.chain().focus().deleteColumn().run()) {
                                editor.chain().focus().deleteTable().run();
                            }
                        }}
                        title="Delete current column"
                        className="rich-text-editor__table-op"
                    >
                        −Col
                    </ToolbarButton>
                    <span className="rich-text-editor__imagebar-sep" aria-hidden="true" />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                        title="Toggle header row"
                        className="rich-text-editor__table-op"
                    >
                        Header
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().mergeCells().run()}
                        disabled={!editor.can().mergeCells()}
                        title="Merge selected cells (drag across cells to select)"
                        className="rich-text-editor__table-op"
                    >
                        Merge
                    </ToolbarButton>
                    <ToolbarButton
                        onClick={() => editor.chain().focus().splitCell().run()}
                        disabled={!editor.can().splitCell()}
                        title="Split merged cell"
                        className="rich-text-editor__table-op"
                    >
                        Split
                    </ToolbarButton>
                    <span className="rich-text-editor__imagebar-sep" aria-hidden="true" />
                    <ToolbarButton
                        onClick={() => editor.chain().focus().deleteTable().run()}
                        title="Delete table"
                        className="rich-text-editor__btn-danger"
                    >
                        <TrashIcon />
                    </ToolbarButton>
                </div>
            )}
            {uploadProgress && (
                <div className="rich-text-editor__status" role="status">{uploadProgress}</div>
            )}
            {isFetchingEmbedDetails && !uploadProgress && (
                <div className="rich-text-editor__status" role="status">Fetching video thumbnail…</div>
            )}
            {uploadError && (
                <div className="rich-text-editor__status error" role="alert">{uploadError}</div>
            )}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelection}
                style={{ display: 'none' }}
            />
            <input
                ref={posterInputRef}
                type="file"
                accept="image/*"
                onChange={handlePosterFileSelection}
                style={{ display: 'none' }}
            />
            <EditorContent editor={editor} />
        </div>
    );
};

export default RichTextEditor;
